/**
 * Secure Session Management Service
 * Implements secure session handling with automatic expiration and renewal
 */

import { auditLogger } from './auditLogger';

export interface SessionData {
  sessionId: string;
  userId?: string;
  createdAt: string;
  expiresAt: string;
  lastActivityAt: string;
  metadata?: Record<string, unknown>;
}

export interface SessionConfig {
  sessionTimeout: number; // milliseconds
  renewalThreshold: number; // milliseconds before expiry to renew
  activityTimeout: number; // milliseconds of inactivity before expiry
  storage: 'session' | 'local' | 'memory';
  enabled: boolean;
}

export class SessionExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

class SessionManager {
  private config: SessionConfig;
  private session: SessionData | null = null;
  private activityTimer: NodeJS.Timeout | null = null;
  private renewalTimer: NodeJS.Timeout | null = null;

  constructor(config?: Partial<SessionConfig>) {
    this.config = {
      sessionTimeout: 3600000, // 1 hour
      renewalThreshold: 300000, // 5 minutes
      activityTimeout: 1800000, // 30 minutes of inactivity
      storage: 'session',
      enabled: true,
      ...config,
    };

    // Track user activity
    this.setupActivityTracking();
  }

  /**
   * Initialize or restore a session
   */
  async initialize(userId?: string): Promise<SessionData> {
    if (!this.config.enabled) {
      return this.createDummySession();
    }

    // Try to restore existing session
    const existingSession = this.loadSession();

    if (existingSession && this.isSessionValid(existingSession)) {
      this.session = existingSession;
      this.updateActivity();
      this.setupRenewalTimer();

      await auditLogger.logSecurityEvent('SESSION_RESTORED', 'success', {
        sessionId: this.session.sessionId,
      });

      return this.session;
    }

    // Create new session
    return await this.createSession(userId);
  }

  /**
   * Create a new session
   */
  async createSession(userId?: string): Promise<SessionData> {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + this.config.sessionTimeout);

    this.session = {
      sessionId: this.generateSessionId(),
      userId,
      createdAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      lastActivityAt: now.toISOString(),
    };

    this.saveSession(this.session);
    this.setupRenewalTimer();

    await auditLogger.logSecurityEvent('SESSION_CREATED', 'success', {
      sessionId: this.session.sessionId,
      userId,
    });

    return this.session;
  }

  /**
   * Get current session
   */
  getSession(): SessionData | null {
    if (!this.session) {
      return null;
    }

    if (!this.isSessionValid(this.session)) {
      this.destroySession();
      return null;
    }

    return this.session;
  }

  /**
   * Update session activity timestamp
   */
  updateActivity(): void {
    if (!this.session) {
      return;
    }

    this.session.lastActivityAt = new Date().toISOString();
    this.saveSession(this.session);

    // Reset activity timer
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }

    this.activityTimer = setTimeout(() => {
      this.handleInactivity();
    }, this.config.activityTimeout);
  }

  /**
   * Renew the current session
   */
  async renewSession(): Promise<SessionData> {
    if (!this.session) {
      throw new SessionExpiredError('No active session to renew');
    }

    const now = new Date();
    const expiresAt = new Date(now.getTime() + this.config.sessionTimeout);

    this.session.expiresAt = expiresAt.toISOString();
    this.session.lastActivityAt = now.toISOString();

    this.saveSession(this.session);
    this.setupRenewalTimer();

    await auditLogger.logSecurityEvent('SESSION_RENEWED', 'success', {
      sessionId: this.session.sessionId,
    });

    return this.session;
  }

  /**
   * Destroy the current session
   */
  async destroySession(): Promise<void> {
    if (this.session) {
      await auditLogger.logSecurityEvent('SESSION_DESTROYED', 'success', {
        sessionId: this.session.sessionId,
      });
    }

    this.session = null;
    this.clearStorage();

    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }

    if (this.renewalTimer) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
  }

  /**
   * Update session metadata
   */
  updateMetadata(metadata: Record<string, unknown>): void {
    if (!this.session) {
      return;
    }

    this.session.metadata = {
      ...this.session.metadata,
      ...metadata,
    };

    this.saveSession(this.session);
  }

  /**
   * Check if session is valid
   */
  private isSessionValid(session: SessionData): boolean {
    const now = new Date();
    const expiresAt = new Date(session.expiresAt);
    const lastActivityAt = new Date(session.lastActivityAt);

    // Check expiration time
    if (now > expiresAt) {
      return false;
    }

    // Check activity timeout
    if (now.getTime() - lastActivityAt.getTime() > this.config.activityTimeout) {
      return false;
    }

    return true;
  }

  /**
   * Handle session inactivity
   */
  private async handleInactivity(): Promise<void> {
    await auditLogger.logSecurityEvent('SESSION_INACTIVE', 'warning', {
      sessionId: this.session?.sessionId,
    });

    await this.destroySession();
  }

  /**
   * Setup automatic session renewal timer
   */
  private setupRenewalTimer(): void {
    if (this.renewalTimer) {
      clearTimeout(this.renewalTimer);
    }

    if (!this.session) {
      return;
    }

    const expiresAt = new Date(this.session.expiresAt);
    const now = new Date();
    const timeUntilExpiry = expiresAt.getTime() - now.getTime();
    const renewalTime = timeUntilExpiry - this.config.renewalThreshold;

    if (renewalTime > 0) {
      this.renewalTimer = setTimeout(() => {
        this.renewSession().catch(console.error);
      }, renewalTime);
    }
  }

  /**
   * Setup user activity tracking
   */
  private setupActivityTracking(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleActivity = () => {
      this.updateActivity();
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 15);
    return `ww_${timestamp}_${random}`;
  }

  /**
   * Save session to storage
   */
  private saveSession(session: SessionData): void {
    const storage = this.getStorage();
    if (storage) {
      storage.setItem('ww_session', JSON.stringify(session));
    }
  }

  /**
   * Load session from storage
   */
  private loadSession(): SessionData | null {
    const storage = this.getStorage();
    if (!storage) {
      return null;
    }

    const sessionData = storage.getItem('ww_session');
    if (!sessionData) {
      return null;
    }

    try {
      return JSON.parse(sessionData);
    } catch (error) {
      console.error('[SESSION] Failed to parse session data:', error);
      return null;
    }
  }

  /**
   * Clear session from storage
   */
  private clearStorage(): void {
    const storage = this.getStorage();
    if (storage) {
      storage.removeItem('ww_session');
    }
  }

  /**
   * Get appropriate storage mechanism
   */
  private getStorage(): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }

    switch (this.config.storage) {
      case 'session':
        return sessionStorage;
      case 'local':
        return localStorage;
      case 'memory':
      default:
        return null;
    }
  }

  /**
   * Create a dummy session for when sessions are disabled
   */
  private createDummySession(): SessionData {
    return {
      sessionId: 'disabled',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + this.config.sessionTimeout).toISOString(),
      lastActivityAt: new Date().toISOString(),
    };
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<SessionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Destroy the session manager
   */
  async destroy(): Promise<void> {
    await this.destroySession();
  }
}

// Singleton instance
let sessionManagerInstance: SessionManager | null = null;

export function getSessionManager(config?: Partial<SessionConfig>): SessionManager {
  if (!sessionManagerInstance) {
    sessionManagerInstance = new SessionManager(config);
  }
  return sessionManagerInstance;
}

export const sessionManager = getSessionManager();
